import { Stack, SxProps } from '@mui/material';
import { Form } from 'common/form';
import { AlertCard } from 'common/ui';
import { Header } from 'common/ui/Header';
import { TextBlock } from 'common/ui/TextBlock';
import { TableV2 } from 'components/Table/TableV2';
import { Campaign, Conversation } from 'generated-graphql/types';
import { useCancelCampaign } from 'modules/campaigns/hooks/useCancelCampaign';
import {
  campaignHumanOutreachVariables,
  createCampaignWithParticipantsSchema
} from 'modules/campaigns/forms/createCampaignWithParticipantsSchema';
import { getChildCampaignTitleFromParent } from 'modules/campaigns/utils/getChildCampaignTitleFromParent';
import { ConversationFilterIds } from 'modules/conversations/constants/enums';
import { getFilteredConversations } from 'modules/conversations/utils/getFileteredConversations';
import { mapType } from 'utils/generatedCampaignMappers';
import { getSelectedConversationColumns } from 'modules/conversations/utils/getSelectedConversationColumns';
import { isOngoingConversationInList } from 'modules/conversations/utils/isOngoingConversationInList';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { capitaliseFirstLetter } from 'utils/capitaliseFirstLetter';

const formContainer: SxProps = {
  width: '100%',
  my: 3
};

type LocationState = {
  title?: string;
  customTemplatedMessage?: string;
  conversations: Conversation[];
  campaign: Campaign;
};

export const CampaignCreationWithParticipants: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { conversations, customTemplatedMessage, title, campaign } =
    location.state as LocationState;

  const { showCancelCampaignModal } = useCancelCampaign({
    campaignId: campaign.id
  });

  const [selectedConversations, setSelectedConversations] =
    useState<Conversation[]>(conversations);

  const [filter, setFilter] = useState<
    { value: string; filterId: ConversationFilterIds } | undefined
  >(undefined);

  const removeConversation = (id: string) => {
    const conversationsAfterRemoval = selectedConversations.filter(
      (conversation) => conversation.id !== id
    );

    setSelectedConversations(conversationsAfterRemoval);

    navigate(location, {
      replace: true,
      state: {
        conversations: conversationsAfterRemoval
      }
    });
  };

  const columns = getSelectedConversationColumns({
    onRemoveConversation: removeConversation,
    campaignType: mapType(campaign.type)
  });

  const variables = campaignHumanOutreachVariables.map((variable) => ({
    label: capitaliseFirstLetter(variable.toLowerCase().replaceAll('_', ' ')),
    value: variable
  }));

  const filteredConversations = useMemo(() => {
    return getFilteredConversations(filter, conversations);
  }, [conversations.length, filter?.filterId, filter?.value]);

  return (
    <Stack p={3}>
      <Header
        title={`${conversations.length} candidate${
          conversations.length > 1 ? 's' : ''
        } selected`}
        currentBreadcrumb="Message candidates"
        breadcrumbs={[
          {
            name: 'Campaigns',
            path: Paths.CAMPAIGNS
          }
        ]}
      />
      <Stack>
        <TableV2
          columns={columns}
          data={filteredConversations}
          pagination
          paginationPerPage={10}
          onFilterSelect={(filter) => {
            setFilter({
              value: String(filter.value),
              filterId: filter.filterId as ConversationFilterIds
            });
          }}
          onRemoveFilters={() => {
            setFilter(undefined);
          }}
        />

        <Stack direction="row" spacing="20px" width="70%" mt={6}>
          <TextBlock
            variant="section"
            title="Create message"
            subtitle="Start an SMS conversation. You will be required to control these conversations."
          />
          <Form
            schema={createCampaignWithParticipantsSchema}
            defaultValues={{
              title:
                title ||
                getChildCampaignTitleFromParent({
                  title: campaign.title ?? ''
                }),
              customTemplatedMessage
            }}
            fields={{
              title: {
                type: 'text',
                label: 'Title',
                subLabel: 'Give your campaign a name.'
              },
              customTemplatedMessage: {
                label: 'Choose opening message',
                type: 'templated-text',
                subLabel: `▪ Aim between 200 - 300 characters to maximise engagement
▪ You must include an opt-out message with the "STOP" keyword
▪ Personalise for multiple recipients: type # or { and choose a variable`,
                availableVariables: variables
              }
            }}
            submitButtonProps={{ label: 'Next' }}
            cancelButtonProps={{
              variant: 'tertiary',
              label: 'Cancel'
            }}
            onCancel={() => {
              showCancelCampaignModal();
            }}
            onSubmit={(data) => {
              const state = {
                title: data.title,
                customTemplatedMessage: data.customTemplatedMessage,
                conversations,
                campaign
              };

              // cache the title and custom templated message for this location
              navigate(location, {
                replace: true,
                state
              });

              // navigate to the next page
              navigate(Paths.CAMPAIGN_CREATE_WITH_PARTICIPANTS_REVIEW, {
                state
              });
            }}
            sx={formContainer}
          >
            {isOngoingConversationInList(conversations) && (
              <AlertCard
                title="Conversations in progress will be interrupted"
                type="warning"
              >
                Some candidates will be affected. Check before you continue
              </AlertCard>
            )}
          </Form>
        </Stack>
      </Stack>
    </Stack>
  );
};
